import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ordersAPI } from "../services/api";
import { DashboardLayout } from "../app/layout/DashboardLayout";
import { formatCurrency } from "../utils/formatters";
import { Order, OrderStatus } from "../types";

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<OrderStatus | "">("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const response = await ordersAPI.getOrder(id);
        setOrder(response);
      } catch (err) {
        setError("Sipariş yüklenirken hata oluştu");
        console.error("Error fetching order:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

  const handleStatusUpdate = async () => {
    if (!order || !newStatus) return;

    try {
      await ordersAPI.updateOrderStatus(order.id, { status: newStatus, notes });
      setStatusDialogOpen(false);
      setNewStatus("");
      setNotes("");
      // Refresh order data
      const response = await ordersAPI.getOrder(order.id);
      setOrder(response);
    } catch (err) {
      setError("Sipariş durumu güncellenirken hata oluştu");
      console.error("Error updating order status:", err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "default";
      case "pending_approval":
        return "warning";
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "assigned":
        return "info";
      case "prepared":
        return "info";
      case "in_transit":
        return "secondary";
      case "delivered":
        return "success";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return "Bekliyor";
      case "pending_approval":
        return "Onay Bekliyor";
      case "approved":
        return "Onaylandı";
      case "rejected":
        return "Reddedildi";
      case "assigned":
        return "Atandı";
      case "prepared":
        return "Hazırlandı";
      case "in_transit":
        return "Yolda";
      case "delivered":
        return "Teslim Edildi";
      case "cancelled":
        return "İptal Edildi";
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </Container>
      </DashboardLayout>
    );
  }

  if (error || !order) {
    return (
      <DashboardLayout>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mt: 2 }}>
            {error || "Sipariş bulunamadı"}
          </Alert>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container maxWidth="lg">
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mb: 2 }}
          >
            Geri Dön
          </Button>
          
          <Typography variant="h4" gutterBottom>
            Sipariş #{order.order_number || order.id}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Order Status */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sipariş Durumu
                </Typography>
                <Chip
                  label={getStatusLabel(order.status)}
                  color={getStatusColor(order.status) as any}
                  sx={{ mb: 2 }}
                />
                
                {user?.role === "manager" && order.status === "pending_approval" && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => {
                        setNewStatus("approved");
                        setStatusDialogOpen(true);
                      }}
                      sx={{ mb: 1 }}
                    >
                      Onayla
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={() => {
                        setNewStatus("rejected");
                        setStatusDialogOpen(true);
                      }}
                    >
                      Reddet
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Customer Information */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Müşteri Bilgileri
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <PersonIcon sx={{ mr: 1, color: "text.secondary" }} />
                      <Typography variant="body1">
                        {order.customer_name}
                      </Typography>
                    </Box>
                  </Grid>
                  {order.customer_phone && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <PhoneIcon sx={{ mr: 1, color: "text.secondary" }} />
                        <Typography variant="body1">
                          {order.customer_phone}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {order.customer_email && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <EmailIcon sx={{ mr: 1, color: "text.secondary" }} />
                        <Typography variant="body1">
                          {order.customer_email}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="flex-start" mb={1}>
                      <LocationIcon sx={{ mr: 1, color: "text.secondary", mt: 0.5 }} />
                      <Typography variant="body1">
                        {order.customer_address}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Order Items */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sipariş Kalemleri
                </Typography>
                <List>
                  {order.items?.map((item, index) => (
                    <ListItem key={index} divider>
                      <ListItemText
                        primary={item.product_name}
                        secondary={`${item.quantity} adet x ${formatCurrency(item.unit_price)}`}
                      />
                      <Typography variant="body1" fontWeight="bold">
                        {formatCurrency(item.total_price)}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">Toplam</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {formatCurrency(order.total_amount)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Order Notes */}
          {order.notes && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Notlar
                  </Typography>
                  <Typography variant="body1">
                    {order.notes}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Status Update Dialog */}
        <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)}>
          <DialogTitle>Sipariş Durumu Güncelle</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Notlar (İsteğe bağlı)"
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStatusDialogOpen(false)}>
              İptal
            </Button>
            <Button onClick={handleStatusUpdate} variant="contained">
              Güncelle
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};

export default OrderDetail; 